import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {CostChartData} from '../../../../models/cost-chart-data.interface';
import {COST_COLORS} from '../../../../../constants/charts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';


@Component({
  selector: 'app-cost-benchmark-chart',
  templateUrl: './cost-benchmark-chart.component.html',
  styleUrls: ['./cost-benchmark-chart.component.scss']
})
export class CostBenchmarkChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  csCodes!: string[];
  operationalTextBelowCode = 'kfcharts.WeeeSystem.Cost.BenchmarkOperationalCost.textBelow';
  overheadTextBelowCode = 'kfcharts.WeeeSystem.Cost.BenchmarkOverheadCost.textBelow';
  isAdmin!: boolean;

  @Input()
  data!: CostChartData[];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  weeeSystem!: string;
  @Input()
  lastModifiedDate!: Date;
  @Input()
  type!: string;

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(
      this.type === 'Operational' ? this.operationalTextBelowCode : this.overheadTextBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    if (this.data) {
      this.csCodes = this.data.map(item => item.collectionStream);
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'columnrange',
        },
        colors: COST_COLORS,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        plotOptions: {
          columnrange: {
            dataLabels: {
              enabled: false,
            },
          },
          line: {
            lineWidth: 0,
            states: {
              hover: {
                enabled: false
              }
            }
          },
        },
        tooltip: {
          formatter(): string[] | undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              return ['<div class="center"><b>' + pointOptions.name + '</b></div><br><br>',
                '- Highest cost: €' + formatNumber(pointOptions.custom?.max) + ' /ton' + '<br>',
                '- Lowest cost: €' + formatNumber(pointOptions.custom?.min) + ' /ton' + '<br>',
                '- Average cost per registration: €' + formatNumber(pointOptions.custom?.average) + ' /ton' + '<br>',
                '- Weighted average: €' + formatNumber(pointOptions.custom?.weightedAverage) + ' /ton' + '<br>',
                pointOptions.custom?.mySystemName?.length > 0 ?
                  '- Cost ' + pointOptions.custom?.mySystemName + ': €' + formatNumber(pointOptions.custom?.myValue) + ' /ton' + '<br>'
                  : '',
              ];
            }
            return undefined;
          }
        },
        xAxis: {
          categories: this.csCodes,
          title: {
            text: null
          },
        },
        series: this.convertToChartData(this.data)
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'],
      {state: {code: this.type === 'Operational' ? this.operationalTextBelowCode : this.overheadTextBelowCode}}).then(
      () => {
        // do nothing
      }
    );
  }


  private convertToChartData(data: CostChartData[]): SeriesOptionsType[] {
    const chartData = [] as SeriesOptionsType[];
    this.chart?.ref?.hideData();
    chartData.push({
      type: 'columnrange',
      name: 'Highest-lowest range',
      pointWidth: 8,
      data: data.map(item => {
          return {
            low: Number(item.minValue?.toFixed(2)),
            high: Number(item.maxValue?.toFixed(2)),
            name: item.collectionStream,
            custom: {
              max: item.maxValue,
              min: item.minValue,
              average: item.averageValue,
              weightedAverage: item.weightedAverageValue,
              mySystemName: this.weeeSystem,
              myValue: item.myValue
            }
          };
        }
      )
    });

    // User Weee System
    if (this.weeeSystem?.length > 0) {
      chartData.push({
        name: this.weeeSystem,
        type: 'line',
        lineWidth: 0,
        marker: {
          symbol: 'circle',
          radius: 9,
          color: COST_COLORS[1]
        },
        data: data.map(item => {
          return {
            y: item.myValue || 0,
            name: item.collectionStream,
            custom: {
              max: item.maxValue,
              min: item.minValue,
              average: item.averageValue,
              weightedAverage: item.weightedAverageValue,
              mySystemName: this.weeeSystem,
              myValue: item.myValue
            }
          };
        })
      });
    }


    // Average line
    chartData.push({
      name: 'Average',
      type: 'line',
      lineWidth: 0,
      marker: {
        symbol: 'diamond',
        radius: 8,
        color: COST_COLORS[0]
      },
      data: data.map(item => {
        return {
          y: Number(item.averageValue?.toFixed(2)),
          name: item.collectionStream,
          custom: {
            max: item.maxValue,
            min: item.minValue,
            average: item.averageValue,
            weightedAverage: item.weightedAverageValue,
            mySystemName: this.weeeSystem,
            myValue: item.myValue
          }
        };
      })
    });

    // Weighted average line
    chartData.push({
      name: 'Weighted average',
      type: 'line',
      lineWidth: 0,
      marker: {
        symbol: 'diamond',
        radius: 6,
        color: COST_COLORS[1]
      },
      data: data.map(item => {
        return {
          y: Number(item.weightedAverageValue?.toFixed(2)),
          name: item.collectionStream,
          custom: {
            max: item.maxValue,
            min: item.minValue,
            average: item.averageValue,
            weightedAverage: item.weightedAverageValue,
            mySystemName: this.weeeSystem,
            myValue: item.myValue
          }
        };
      })
    });

    return chartData;
  }


}
